import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import Header from "../../components/Header";
import { dataGridStyle, palette } from "../../theme";
import API from "../../api";
import TicketDetails from "./ticketsDetails/ticketsDetails";
import { HardwareTicketDialog, DeleteTicketDialog, OK_COLOR, WARN_COLOR, PHASES } from "./hardwareTickeets";
import { FilterChips, Muted, SearchField, StatusBadge, TICKET_STATUS, formatDate, parseDate } from "./ticketsUi";

const TYPE_COLOR = { support: palette.primary.light, hardware: "#0d82b8" };

const TYPE_FILTERS = [
  { key: "all", label: "All" },
  { key: "support", label: "Support" },
  { key: "hardware", label: "Hardware" },
];

// Stato unificato: new/working/finished (supporto), open/requested (hardware).
const STATUS_FILTERS = [
  { key: "any", label: "Any status" },
  { key: "new", label: "New" },
  { key: "working", label: "Working" },
  { key: "finished", label: "Finished" },
  { key: "requested", label: "Replacement requested" },
];
const SUPPORT_STATUS_KEY = { 1: "new", 2: "working", 3: "finished" };

const TicketR = () => {
  const [supportTickets, setSupportTickets] = useState([]);
  const [hardwareTickets, setHardwareTickets] = useState([]);
  const [supportLoaded, setSupportLoaded] = useState(false);
  const [hardwareLoaded, setHardwareLoaded] = useState(false);

  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("any");
  const [search, setSearch] = useState("");

  // Dettaglio ticket di supporto (sostituisce la lista) / dialog hardware.
  const [card, setCard] = useState(false);
  const [tick, setTick] = useState({});
  const [hardwareOpen, setHardwareOpen] = useState(null);
  const [hardwareToDelete, setHardwareToDelete] = useState(null);

  const commercial = sessionStorage.getItem("commercial");

  const loadHardware = useCallback(() => {
    return API.getHardwareTickets()
      .then((res) => setHardwareTickets(res.data.map((item) => ({ id: item._id, ...item }))))
      .catch((err) => console.error("[Tickets] hardware tickets fetch error:", err))
      .finally(() => setHardwareLoaded(true));
  }, []);

  useEffect(() => {
    API.getTicketsRes({})
      .then((result) => {
        setSupportTickets(result.data.map((item) => ({ id: item._id, ...item })));
      })
      .catch((error) => {
        console.error("[Tickets] support tickets fetch error:", error);
      })
      .finally(() => setSupportLoaded(true));

    loadHardware();
  }, [loadHardware]);

  // Righe unificate. `raw` = ticket originale, usato dai dettagli.
  const allRows = useMemo(() => {
    const support = supportTickets.map((t) => ({
      id: `s-${t.id}`,
      kind: "support",
      name: t.ragioneSociale,
      subject: t.oggetto,
      email: t.fkEmail,
      statusKey: SUPPORT_STATUS_KEY[t.stato] ?? "",
      ts: parseDate(t.dataCreazione)?.ts ?? 0,
      dateLabel: formatDate(t.dataCreazione),
      raw: t,
    }));
    const hardware = hardwareTickets.map((t) => {
      const when = t.lastUpdate || t.dataCreazione;
      return {
        id: `h-${t.id}`,
        kind: "hardware",
        name: t.ragioneSociale,
        subject: t.matricola ? `Hardware verification · S.N ${t.matricola}` : "Hardware verification",
        email: "",
        serial: t.matricola,
        statusKey: t.requestNewLecs ? "requested" : "open",
        ts: parseDate(when)?.ts ?? 0,
        dateLabel: formatDate(when),
        raw: t,
      };
    });
    return [...support, ...hardware];
  }, [supportTickets, hardwareTickets]);

  const typeCounts = useMemo(() => {
    const c = { all: allRows.length, support: 0, hardware: 0 };
    allRows.forEach((r) => { c[r.kind] += 1; });
    return c;
  }, [allRows]);

  // Conteggi di stato sempre sul totale del tipo selezionato, non sulla vista filtrata.
  const statusCounts = useMemo(() => {
    const c = { any: 0, new: 0, working: 0, finished: 0, requested: 0 };
    allRows.forEach((r) => {
      if (typeFilter !== "all" && r.kind !== typeFilter) return;
      c.any += 1;
      if (c[r.statusKey] !== undefined) c[r.statusKey] += 1;
    });
    return c;
  }, [allRows, typeFilter]);

  // Più recenti prima; ricerca testuale semplice.
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allRows
      .filter((r) => typeFilter === "all" || r.kind === typeFilter)
      .filter((r) => statusFilter === "any" || r.statusKey === statusFilter)
      .filter(
        (r) =>
          !q ||
          [r.name, r.email, r.subject, r.serial].some((v) => String(v ?? "").toLowerCase().includes(q))
      )
      .sort((a, b) => b.ts - a.ts);
  }, [allRows, typeFilter, statusFilter, search]);

  const openRow = (row) => {
    if (row.kind === "support") {
      setTick(row.raw);
      setCard(true);
    } else {
      setHardwareOpen(row.raw);
    }
  };

  const handleCloseHardware = () => {
    setHardwareOpen(null);
    loadHardware(); // le fasi possono essere cambiate nel dialog
  };

  const handleConfirmDelete = () => {
    const id = hardwareToDelete.id;
    setHardwareToDelete(null);
    // Ricarico la lista dopo la cancellazione, non in parallelo.
    API.deleteTicket({ id })
      .catch(() => {})
      .finally(loadHardware);
  };

  const columns = [
    {
      field: "kind",
      headerName: "Type",
      width: 110,
      renderCell: (p) => (
        <StatusBadge color={TYPE_COLOR[p.value]}>{p.value === "support" ? "Support" : "Hardware"}</StatusBadge>
      ),
    },
    {
      field: "name",
      headerName: "Name",
      minWidth: 160,
      flex: 1,
      renderCell: (p) => p.value || <Muted>-</Muted>,
    },
    {
      field: "subject",
      headerName: "Subject",
      minWidth: 240,
      flex: 1.6,
      renderCell: (p) => p.value || <Muted>-</Muted>,
    },
    {
      field: "email",
      headerName: "Email",
      minWidth: 190,
      flex: 1,
      renderCell: (p) => p.value || <Muted>-</Muted>,
    },
    {
      field: "statusKey",
      headerName: "Status",
      width: 190,
      renderCell: (p) => {
        if (p.row.kind === "support") {
          const s = TICKET_STATUS[p.row.raw.stato];
          return s ? <StatusBadge color={s.color}>{s.label}</StatusBadge> : <Muted>-</Muted>;
        }
        if (p.row.raw.requestNewLecs) return <StatusBadge color={WARN_COLOR}>Replacement requested</StatusBadge>;
        const n = Number(p.row.raw.fase) || 0;
        return (
          <StatusBadge color={n >= PHASES.length ? OK_COLOR : n > 0 ? palette.primary.light : "#888888"}>
            {`Phase ${n}/${PHASES.length}`}
          </StatusBadge>
        );
      },
    },
    {
      field: "ts",
      headerName: "Date",
      width: 160,
      renderCell: (p) => p.row.dateLabel,
    },
    {
      field: "actions",
      headerName: "",
      width: 150,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: "right",
      renderCell: (p) => (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5, width: "100%" }}>
          <Button
            variant="outlined"
            size="small"
            sx={{ minWidth: 84 }}
            onClick={(e) => {
              e.stopPropagation();
              openRow(p.row);
            }}
          >
            {p.row.kind === "support" ? "Read" : "Details"}
          </Button>
          {/* Slot fisso per il cestino: la colonna resta allineata anche senza icona */}
          <Box sx={{ width: 30, height: 30, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
            {p.row.kind === "hardware" && !p.row.raw.requestNewLecs && (
              <Tooltip title="Delete ticket">
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setHardwareToDelete(p.row.raw);
                  }}
                  aria-label="Delete ticket"
                  size="small"
                  sx={{ width: 30, height: 30, p: 0, color: "error.main", "&:hover": { backgroundColor: "rgba(211, 47, 47, 0.1)" } }}
                >
                  <DeleteForeverIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      ),
    },
  ];

  if (commercial === "true") return <Typography>Not Authorized</Typography>;

  const toolbar = (
    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
      <SearchField value={search} onChange={setSearch} placeholder="Search name, email, subject, serial…" />
      <FilterChips
        options={TYPE_FILTERS.map((f) => ({ ...f, count: typeCounts[f.key] }))}
        value={typeFilter}
        onChange={(key) => {
          setTypeFilter(key);
          setCard(false);
        }}
      />
      <Box sx={{ width: "1px", alignSelf: "stretch", backgroundColor: "rgba(255,255,255,0.12)" }} />
      <FilterChips
        options={STATUS_FILTERS.map((f) => ({ ...f, count: statusCounts[f.key] }))}
        value={statusFilter}
        onChange={(key) => {
          setStatusFilter(key);
          setCard(false);
        }}
      />
    </Stack>
  );

  return (
    <Box m="20px">
      <Box mb={2}>
        <Header title="Tickets" subtitle="" />
      </Box>

      <HardwareTicketDialog ticket={hardwareOpen} onClose={handleCloseHardware} />
      <DeleteTicketDialog
        ticket={hardwareToDelete}
        onCancel={() => setHardwareToDelete(null)}
        onConfirm={handleConfirmDelete}
      />

      {card ? (
        <TicketDetails
          subject={tick.oggetto}
          name={tick.ragioneSociale}
          email={tick.fkEmail}
          id={tick.id}
          data={tick.dataCreazione}
          stato={tick.stato}
          tickets={supportTickets}
          setTickets={setSupportTickets}
          setCard={setCard}
        />
      ) : (
        <>
          <Box mb={2}>{toolbar}</Box>
          <Box sx={{ height: "calc(100vh - 270px)", minHeight: 420 }}>
            <DataGridPro
              sx={{
                ...dataGridStyle,
                border: "none",
                borderRadius: 0,
                "& .MuiDataGrid-row": { cursor: "pointer" },
              }}
              loading={!supportLoaded || !hardwareLoaded}
              rows={rows}
              columns={columns}
              disableRowSelectionOnClick
              localeText={{ noRowsLabel: "No tickets found" }}
              onRowClick={(params) => openRow(params.row)}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default TicketR;
